import { useMemo } from "react";
import { array, boolean, date, number, object, string } from "yup";
import {
    type EventStatusEnum,
    type Form,
    FormFieldsFieldTypeEnum,
} from "../../apiClient/generated";
import { type Context, evaluateCondition } from "./conditionEvaluator";
import type { TableColumn } from "./formDefinition";

// Validation rules are `values.*`-only by design: emissionRecord/extra are
// reserved for suggestions and visibility, which never run inside yup tests.
const validationContext = (rootValues: unknown): Context =>
    ({ values: rootValues ?? {} }) as unknown as Context;

const COLUMN_TYPE_MAP = {
    string: () => string().nullable(),
    number: () => number().nullable(),
    boolean: () => boolean().nullable(),
    date: () => date().nullable(),
    datetime: () => date().nullable(),
    choice: (col: TableColumn) =>
        string()
            .oneOf([...(col.choices ?? []), null, ""])
            .nullable(),
};

function buildTableSchema(columns: TableColumn[]) {
    const shape = Object.fromEntries(
        columns.map((col) => [
            col.field_name,
            COLUMN_TYPE_MAP[col.field_type]?.(col) ?? string().nullable(),
        ]),
    );
    return array().of(object(shape)).nullable();
}

// Define base schema as a constant for reusability
const baseFormSchema = object({
    similarEmission: string().nullable(),
    // Responsible
    responsible: number().when("eventStatus", {
        is: (eventStatus: EventStatusEnum) =>
            eventStatus === "NEW" ||
            eventStatus === "WORK_IN_PROGRESS" ||
            eventStatus === "WAITING_APPROVAL" ||
            eventStatus === "COMPLETED" ||
            eventStatus === "CREATED",
        then: (schema) => schema.required("Field required."),
        otherwise: (schema) => schema.notRequired(),
    }),
    reviewer: number().nullable(),
    notifyResponsible: boolean(),
    // Statuses - they always start out blank
    eventStatus: string().nullable(),
    // Event dates - empty string from form inputs maps to null (no Invalid Date)
    startDate: date()
        .nullable()
        .transform((value, input) => (input === "" ? null : value)),
    endDate: date()
        .nullable()
        .transform((value, input) => (input === "" ? null : value)),
    // Infrastructure
    site: string().nullable(),
    equipment: string().nullable(),
    extraNotes: string(),
    formData: object().optional().nullable(),
});

/**
 * useFormValidationSchema
 *
 * Builds a Yup validation schema by extending the base event schema
 * with dynamic fields from attached custom forms (formData).
 */
export const useFormValidationSchema = (enabledForms: Form[]) => {
    return useMemo(() => {
        let schema = baseFormSchema;

        // Build formData validation (new form-level custom fields)
        // First pass: Group fields by name to handle duplicates
        const fieldsByName: Record<
            string,
            Array<{ field: any; formId: number }>
        > = {};

        if (enabledForms && enabledForms.length > 0) {
            enabledForms.forEach((form, formIndex) => {
                form.fields?.forEach((field) => {
                    if (!fieldsByName[field.fieldName]) {
                        fieldsByName[field.fieldName] = [];
                    }
                    fieldsByName[field.fieldName].push({
                        field,
                        formId: formIndex,
                    });
                });
            });
        }

        // Second pass: Build merged schemas with AND logic for duplicate fields
        const formDataShape: Record<string, any> = {};

        Object.entries(fieldsByName).forEach(
            ([fieldName, fieldDefinitions]) => {
                // Use the first field's type (they should all be the same)
                const firstField = fieldDefinitions[0].field;
                let fieldSchema: any;

                // Create base schema based on field type
                if (firstField.fieldType === FormFieldsFieldTypeEnum.Number) {
                    fieldSchema = number().nullable();
                } else if (
                    firstField.fieldType === FormFieldsFieldTypeEnum.String
                ) {
                    fieldSchema = string().nullable();
                } else if (
                    firstField.fieldType === FormFieldsFieldTypeEnum.Choice
                ) {
                    fieldSchema = string().nullable();
                } else if (
                    firstField.fieldType === FormFieldsFieldTypeEnum.Boolean
                ) {
                    fieldSchema = boolean().nullable();
                } else if (
                    firstField.fieldType === FormFieldsFieldTypeEnum.Datetime
                ) {
                    fieldSchema = date()
                        .nullable()
                        .transform((value, input) =>
                            input === "" ? null : value,
                        );
                } else if (
                    firstField.fieldType === FormFieldsFieldTypeEnum.Date
                ) {
                    fieldSchema = date()
                        .nullable()
                        .transform((value, input) =>
                            input === "" ? null : value,
                        );
                } else if (
                    firstField.fieldType === FormFieldsFieldTypeEnum.Table
                ) {
                    const columns = firstField.fieldOptions?.columns ?? [];
                    fieldSchema = buildTableSchema(columns);
                } else if (
                    firstField.fieldType === FormFieldsFieldTypeEnum.User
                ) {
                    fieldSchema = number().nullable();
                } else if (
                    firstField.fieldType === FormFieldsFieldTypeEnum.File
                ) {
                    // File uploads are handled separately — skip validation here
                    return;
                } else {
                    // Unknown field type - skip this field
                    return;
                }

                // Apply validation for each form that defines this field (AND logic)
                // Each form's requirements are checked independently - all must pass
                fieldDefinitions.forEach(({ field, formId }) => {
                    if (field.requiredCondition?.logic || field.isRequired) {
                        fieldSchema = fieldSchema.test(
                            `conditional-required-form-${formId}-${field.fieldName}`,
                            "Field required.",
                            function (value) {
                                const rootValues = this.from?.[1]?.value;
                                const eventStatus = rootValues?.eventStatus;

                                // Only validate for COMPLETED or WAITING_APPROVAL
                                if (
                                    eventStatus !== "COMPLETED" &&
                                    eventStatus !== "WAITING_APPROVAL" &&
                                    eventStatus !== "REJECTED"
                                ) {
                                    return true;
                                }

                                const hasValue = value != null && value !== "";

                                // Handle conditional requirement
                                if (field.requiredCondition?.logic) {
                                    const conditionMet = evaluateCondition(
                                        field.requiredCondition.logic,
                                        validationContext(rootValues),
                                    );
                                    const isRequired =
                                        conditionMet || field.isRequired;
                                    return !isRequired || hasValue;
                                }

                                // Handle basic required
                                return !field.isRequired || hasValue;
                            },
                        );
                    }
                });

                formDataShape[fieldName] = fieldSchema;
            },
        );

        // Always extend schema with formData (even if empty)
        schema = schema.shape({
            formData: object().shape(formDataShape).nullable(),
        });

        return schema;
    }, [enabledForms]);
};
